import { Injectable, signal, WritableSignal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { AlertController } from '@ionic/angular/standalone';
import { NativeSettings, IOSSettings, AndroidSettings } from 'capacitor-native-settings';

const ONBOARDING_KEY = 'pax_permissions_onboarding';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  public permissionsStatus: WritableSignal<boolean> = signal(false);

  constructor(private alertController: AlertController) {
    this.checkInitialStatus();
  }

  // ── Internal status check ────────────────────────────────────────────────

  private async checkInitialStatus(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      this.permissionsStatus.set(true);
      return;
    }
    try {
      const loc   = await Geolocation.checkPermissions();
      const notif = await LocalNotifications.checkPermissions();
      this.permissionsStatus.set(loc.location === 'granted' && notif.display === 'granted');
    } catch {
      this.permissionsStatus.set(false);
    }
  }

  // ── Onboarding ───────────────────────────────────────────────────────────

  async hasCompletedOnboarding(): Promise<boolean> {
    const { value } = await Preferences.get({ key: ONBOARDING_KEY });
    return value === 'true';
  }

  /**
   * Called on every app launch. Checks actual device state first and only
   * shows dialogs when something is missing — handles revocations between sessions.
   */
  async ensurePermissions(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      this.permissionsStatus.set(true);
      await Preferences.set({ key: ONBOARDING_KEY, value: 'true' });
      return;
    }

    let allGranted = false;
    try {
      const loc   = await Geolocation.checkPermissions();
      const notif = await LocalNotifications.checkPermissions();
      allGranted  = loc.location === 'granted' && notif.display === 'granted';
    } catch { /* treat as not granted */ }

    if (!allGranted) {
      await this.repairPermissions(false);
    }

    await Preferences.set({ key: ONBOARDING_KEY, value: 'true' });
  }

  // ── Camera permission (for QR scanner) ──────────────────────────────────

  async requestCameraPermission(manual = false): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      return true;
    } catch (e: any) {
      if (e?.name === 'NotAllowedError' && manual) {
        await this.showPermissionAlert(
          'Cámara desactivada',
          'Para escanear el QR del bus necesitamos acceso a la cámara.',
          'Cámara'
        );
      }
      return false;
    }
  }

  // ── Main repair — mirrors the driver app's logic exactly ─────────────────

  /**
   * Requests any missing permissions in order: Location → Notifications → Camera.
   * Returns true when both location and notifications are granted.
   */
  async repairPermissions(manual = false): Promise<boolean> {
    if (Capacitor.getPlatform() === 'web') {
      this.permissionsStatus.set(true);
      return true;
    }

    // ── 1. Location ──────────────────────────────────────────────────────────
    try {
      const status = await Geolocation.checkPermissions();

      if (status.location === 'granted') {
        // Already granted — nothing to do
      } else if (status.location === 'denied') {
        if (manual) {
          await this.showPermissionAlert(
            'Acceso a la Ubicación',
            'Para mostrarte las rutas y buses cercanos necesitamos tu ubicación. ' +
            'Selecciona "Al usar la app" en Ajustes.',
            'Ubicación'
          );
        }
      } else {
        // 'prompt' or 'prompt-with-rationale' → show native dialog
        await Geolocation.requestPermissions({ permissions: ['location'] });
      }
    } catch (e) {
      console.error('[Permisos] Error de ubicación:', e);
    }

    // ── 2. Notifications ─────────────────────────────────────────────────────
    try {
      const notifStatus = await LocalNotifications.checkPermissions();

      if (notifStatus.display === 'prompt') {
        const result = await LocalNotifications.requestPermissions();
        if (result.display === 'granted') {
          await this.createNotificationChannel();
        }
      } else if (notifStatus.display === 'denied' && manual) {
        await this.showPermissionAlert(
          'Notificaciones desactivadas',
          'Actívalas en Ajustes para recibir alertas de tu bus.',
          'Notificaciones'
        );
      }
    } catch (e) {
      console.error('[Permisos] Error de notificaciones:', e);
    }

    // ── 3. Camera ────────────────────────────────────────────────────────────
    // Disabled at launch: the QR scanner it serves is not shipping yet, and
    // prompting for a permission no visible feature uses fails App Store
    // review (5.1.1). Re-enable from the scanner page when QR launches.
    // await this.requestCameraPermission(manual);

    // ── 4. Update signal & return result ─────────────────────────────────────
    try {
      const loc   = await Geolocation.checkPermissions();
      const notif = await LocalNotifications.checkPermissions();
      const ok    = loc.location === 'granted' && notif.display === 'granted';
      this.permissionsStatus.set(ok);
      return ok;
    } catch {
      this.permissionsStatus.set(false);
      return false;
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private async showPermissionAlert(
    header: string,
    message: string,
    _settingsCtx: string
  ): Promise<void> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        { text: 'Ahora no', role: 'cancel' },
        {
          text: 'Ir a Ajustes',
          handler: () => {
            NativeSettings.open({
              optionAndroid: AndroidSettings.ApplicationDetails,
              optionIOS:     IOSSettings.App,
            });
          },
        },
      ],
      mode: 'ios',
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  private async openSettings(): Promise<void> {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS:     IOSSettings.App,
    });
  }

  private async createNotificationChannel(): Promise<void> {
    try {
      await LocalNotifications.createChannel({
        id:          'pax_general',
        name:        'Alertas syncc',
        description: 'Alertas de servicio de Sale',
        importance:  4,
        vibration:   true,
        visibility:  1,
      });
    } catch {}
  }
}
